import { proxy, useSnapshot } from 'valtio'
import { useSearchParam } from 'react-use'

import {
  subscribeToLiveData,
  useLiveDataQuery,
  type LiveData,
} from '../global/blocs/useLiveData'

import {
  getNextAudienceLiveState,
  getWaitingRoomType,
  isWebinarOverFromLiveData,
  isWebinarTimePastFromLiveData,
  resolveHasWitnessedLiveBroadcast,
  shouldRedirectAfterWebinar,
  shouldShowAudienceWaitingRoom,
} from './liveState'

const audienceLiveState = proxy({
  hasBeenBroadcasting: false,
  isWebinarOver: false,
  isWebinarTimePast: false,
  hasRedirected: false,
})

let unsubscribeQueryCache: (() => void) | null = null

const resolveIsWebinarOver = (
  latched: boolean,
  liveData: LiveData | undefined,
  showThankYou: boolean,
  hasBeenBroadcastingLatched: boolean
) =>
  latched ||
  showThankYou ||
  isWebinarOverFromLiveData(liveData, hasBeenBroadcastingLatched)

const resolveHasBeenBroadcasting = (
  latched: boolean,
  liveData: LiveData | undefined,
  isWebinarOver: boolean
) => latched || liveData?.broadcasting_p || isWebinarOver

const resolveIsWebinarTimePast = (
  latched: boolean,
  liveData: LiveData | undefined
) => latched || isWebinarTimePastFromLiveData(liveData)

const latchAudienceLiveStateFromLiveData = (liveData: LiveData) => {
  Object.assign(
    audienceLiveState,
    getNextAudienceLiveState(audienceLiveState, liveData)
  )
}

const maybeRedirectAfterWebinar = (liveData: LiveData) => {
  if (!shouldRedirectAfterWebinar(liveData, audienceLiveState)) {
    return
  }

  audienceLiveState.hasRedirected = true
  window.location.href = liveData.after_webinar_link
}

const syncAudienceLiveStateFromLiveData = (liveData: LiveData | undefined) => {
  if (!liveData) return

  latchAudienceLiveStateFromLiveData(liveData)
  maybeRedirectAfterWebinar(liveData)
}

const tearDownAudienceLiveStateSubscription = () => {
  if (unsubscribeQueryCache) {
    unsubscribeQueryCache()
    unsubscribeQueryCache = null
  }
}

const ensureAudienceLiveStateSubscription = () => {
  if (!unsubscribeQueryCache) {
    unsubscribeQueryCache = subscribeToLiveData(
      syncAudienceLiveStateFromLiveData
    )
  }

  return tearDownAudienceLiveStateSubscription
}

const markWebinarTimePast = () => {
  audienceLiveState.isWebinarTimePast = true
}

const useIsWebinarOver = () => {
  const { isWebinarOver: latched, hasBeenBroadcasting: latchedBroadcasting } =
    useSnapshot(audienceLiveState)
  const { data: liveData } = useLiveDataQuery()
  const showThankYou = useSearchParam('show_thank_you') === '1'

  return resolveIsWebinarOver(
    latched,
    liveData,
    showThankYou,
    latchedBroadcasting
  )
}

const useHasWitnessedLiveBroadcast = () => {
  const { hasBeenBroadcasting: latched } = useSnapshot(audienceLiveState)
  const { data: liveData } = useLiveDataQuery()

  return resolveHasWitnessedLiveBroadcast(latched, liveData)
}

const useHasBeenBroadcasting = () => {
  const { hasBeenBroadcasting: latched } = useSnapshot(audienceLiveState)
  const { data: liveData } = useLiveDataQuery()
  const isWebinarOver = useIsWebinarOver()

  return resolveHasBeenBroadcasting(latched, liveData, isWebinarOver)
}

const useIsWebinarTimePast = () => {
  const { isWebinarTimePast: latched } = useSnapshot(audienceLiveState)
  const { data: liveData } = useLiveDataQuery()

  return resolveIsWebinarTimePast(latched, liveData)
}

const useWaitingRoomType = () => {
  const { data: liveData } = useLiveDataQuery()
  const isWebinarOver = useIsWebinarOver()

  return getWaitingRoomType(liveData, isWebinarOver)
}

const useShowAudienceWaitingRoom = () => {
  const { data: liveData } = useLiveDataQuery()
  const isWebinarOver = useIsWebinarOver()
  const hasWitnessedLiveBroadcast = useHasWitnessedLiveBroadcast()

  return shouldShowAudienceWaitingRoom(
    liveData,
    isWebinarOver,
    hasWitnessedLiveBroadcast
  )
}

export {
  ensureAudienceLiveStateSubscription,
  markWebinarTimePast,
  useIsWebinarOver,
  useHasWitnessedLiveBroadcast,
  useHasBeenBroadcasting,
  useIsWebinarTimePast,
  useWaitingRoomType,
  useShowAudienceWaitingRoom,
}
